/**
 * Centralized constants for touch and pointer interaction across the app.
 *
 * All timing windows, distance thresholds, and touch-related constants live
 * here so they can be tuned in one place.
 */

// ---------------------------------------------------------------------------
// Double-tap detection
// ---------------------------------------------------------------------------

/** Maximum time (ms) between two taps for a double-tap to register. */
export const DOUBLE_TAP_MS = 500;

/** Maximum distance squared (px²) between two taps for a double-tap. */
export const DOUBLE_TAP_DIST_SQ = 1600; // 40px

// ---------------------------------------------------------------------------
// Multi-finger tap detection (undo/redo)
// ---------------------------------------------------------------------------

/** Maximum duration (ms) of a multi-finger tap gesture. */
export const MULTI_TAP_MAX_DURATION = 400;

/** Maximum movement (px) per finger for a multi-finger tap. */
export const MULTI_TAP_MAX_MOVE = 25;

// ---------------------------------------------------------------------------
// Pointer tap vs. drag threshold
// ---------------------------------------------------------------------------

/** Distance² (px²) a pointer must move to switch from tap to drag. */
export const TAP_DRAG_THRESHOLD_SQ = 64; // 8px

// ---------------------------------------------------------------------------
// Box select hold-to-drag
// ---------------------------------------------------------------------------

/** Duration (ms) the pointer must be held still before box select activates. */
export const BOX_SELECT_HOLD_MS = 200;

// ---------------------------------------------------------------------------
// Hold-to-add (touch additive selection)
// ---------------------------------------------------------------------------

/**
 * Duration (ms) a touch pointer must be held still on an element for the
 * tap to be treated as additive (like Shift+click) instead of replacing
 * the selection. Only applies to touch input — mouse users have Shift.
 */
export const HOLD_TO_ADD_MS = 200;

// ---------------------------------------------------------------------------
// Z-depth selection cycling (Blender-style click-through)
// ---------------------------------------------------------------------------

/**
 * Distance² (px²) between consecutive clicks that still counts as the "same
 * spot" for cycling through overlapping objects. ~6px radius — loose enough
 * to tolerate natural hand jitter, tight enough that an intentional second
 * click on a neighbouring object resets the cycle.
 */
export const CYCLE_CLICK_DIST_SQ = 36;

/**
 * Maximum time (ms) between consecutive clicks to advance the selection
 * cycle. Longer than the double-tap window so a deliberate re-click still
 * registers, but short enough that a fresh click after a pause starts over.
 */
export const CYCLE_CLICK_MS = 700;

// ---------------------------------------------------------------------------
// Precision transform
// ---------------------------------------------------------------------------

/**
 * Mouse-delta multiplier applied once precision mode is active (either via
 * Shift held during a drag, or the stillness-hold timer in the gizmo path).
 * Quarter-speed gives fine-grained control without losing the coarse range.
 */
export const PRECISION_SCALE = 0.25;

/**
 * Duration (ms) the pointer must remain still during a gizmo drag before
 * precision mode engages automatically. Applies to all pointer types.
 */
export const PRECISION_HOLD_MS = 1000;

/**
 * Per-event movement threshold² (px²): if the pointer moves more than this
 * between consecutive events the stillness timer resets, so precision mode
 * can be entered at any point during a drag (not just at drag start).
 */
export const PRECISION_STILL_THRESHOLD_SQ = 16;

// ---------------------------------------------------------------------------
// Edge-pan (camera rig compensation while dragging a transform gizmo)
// ---------------------------------------------------------------------------

/**
 * Distance (px) inward from each viewport edge that counts as the pan zone.
 * Smaller values make compensation feel more intentional — the cursor must
 * be genuinely near the edge, not merely "in the outer third".
 */
export const EDGE_PAN_ZONE_PX = 44;

/**
 * Dwell time (ms) the pointer must spend continuously inside the pan zone
 * before the camera actually begins panning. Prevents incidental brushes
 * past the edge from triggering unwanted camera movement.
 */
export const EDGE_PAN_HOLD_MS = 400;

/** Maximum camera-follow speed (screen px / sec) at the very edge of the viewport. */
export const EDGE_PAN_MAX_SPEED = 210;

/** Exponential decay rate for velocity lerp (higher = snappier response). */
export const EDGE_PAN_DECAY = 8;

/** Velocity below which the edge-pan RAF loop treats the gizmo as stationary. */
export const EDGE_PAN_MIN_ENGAGE_VEL = 0.5;

// ---------------------------------------------------------------------------
// Touch device detection
// ---------------------------------------------------------------------------

/**
 * Returns true when the primary pointing device is coarse (touch).
 * Used to skip auto-focus on text inputs that would trigger the on-screen
 * keyboard and obstruct the user's view.
 */
export function hasTouchScreen(): boolean {
  return window.matchMedia("(pointer: coarse)").matches;
}
